/**
 * DashboardPage — / (home)
 * The workspace overview on the DashboardPage template. Demo pages: / (home), /analytics and /saas: headline stats,
 * a wide chart card, side "top" lists, then module cards. Template example: NextAdmin `(dashboard)/(home)/page.tsx`
 * (`overview-stats`, `sales-chart`, `top-products`). Every widget reads a generated query and has its own skeleton.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, Brain, CheckCircle2, Clock, Play, Workflow } from 'lucide-react';
import {
  listActiveAgentsOptions,
  listAgentWorkflowRunsOptions,
  listAgentWorkflowsOptions,
  listMyModulesOptions,
  listPendingDecisionsOptions,
} from '../generated/api/@tanstack/react-query.gen.js';
import type { DecisionRequest, WorkflowRun } from '../generated/api/types.gen.js';
import { DashboardGrid, DashboardPage as DashboardTemplate } from '../components/page/index.js';
import { BarChart, ChartCard } from '../components/charts/index.js';
import { Badge, type BadgeVariant } from '../components/ui/Badge.js';
import { Card } from '../components/ui/Card.js';
import { ItemList, type ItemListItem } from '../components/ui/ItemList.js';
import { QueryBoundary } from '../components/ui/QueryBoundary.js';
import { Skeleton } from '../components/ui/Skeleton.js';
import type { StatStripCell } from '../components/ui/StatStrip.js';
import { evaluateClientPolicy } from '../auth/policies.js';
import { resolveIcon } from '../modules/icons.js';
import { useAuthStore } from '../store/index.js';

const DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

const PRIORITY: Record<DecisionRequest['priority'], { variant: BadgeVariant; label: string }> = {
  urgent: { variant: 'danger', label: 'Urgent' },
  high: { variant: 'warning', label: 'High' },
  medium: { variant: 'info', label: 'Medium' },
  low: { variant: 'neutral', label: 'Low' },
};

const isSucceeded = (run: WorkflowRun) => run.status === 'completed' || run.status === 'succeeded';

/** Runs per day over the last two weeks, split into finished, failed and other. */
function runsByDay(runs: readonly WorkflowRun[], now: number) {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const first = startOfToday.getTime() - (DAYS - 1) * DAY_MS;
  const days = Array.from({ length: DAYS }, (_, i) => ({
    day: new Date(first + i * DAY_MS).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    succeeded: 0,
    failed: 0,
    other: 0,
  }));
  for (const run of runs) {
    const index = Math.floor((new Date(run.startedAt).getTime() - first) / DAY_MS);
    const bucket = days[index];
    if (!bucket) continue;
    if (isSucceeded(run)) bucket.succeeded += 1;
    else if (run.status === 'failed') bucket.failed += 1;
    else bucket.other += 1;
  }
  return days;
}

function ListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-4" role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton width={36} height={36} rounded="rounded-lg" className="shrink-0" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton height={12} width="55%" />
            <Skeleton height={10} width="35%" />
          </div>
          <Skeleton height={20} width={56} rounded />
        </div>
      ))}
    </div>
  );
}

function RunsChartCard() {
  const runsQuery = useQuery(listAgentWorkflowRunsOptions());
  const [now] = useState(() => Date.now());
  return (
    <QueryBoundary
      query={runsQuery}
      label="workflow runs"
      loading={
        <ChartCard title="Workflow runs" description="Last 14 days">
          <Skeleton height={260} rounded="rounded-lg" />
        </ChartCard>
      }
    >
      {(response) => {
        const runs = response.data ?? [];
        const days = runsByDay(runs, now);
        const recent = days.reduce((sum, d) => sum + d.succeeded + d.failed + d.other, 0);
        const failed = days.reduce((sum, d) => sum + d.failed, 0);
        const succeeded = days.reduce((sum, d) => sum + d.succeeded, 0);
        return (
          <ChartCard
            title="Workflow runs"
            description="Last 14 days"
            stats={[
              { id: 'runs', label: 'Runs', value: String(recent), tone: 'accent' },
              { id: 'succeeded', label: 'Succeeded', value: String(succeeded), tone: 'success' },
              { id: 'failed', label: 'Failed', value: String(failed), tone: 'danger' },
            ]}
          >
            <BarChart
              data={days}
              xKey="day"
              series={[
                { key: 'succeeded', label: 'Succeeded', tone: 'success' },
                { key: 'failed', label: 'Failed', tone: 'danger' },
                { key: 'other', label: 'Other', tone: 'muted' },
              ]}
              stacked
              title="Workflow runs per day"
              summary={`${recent} runs in the last ${DAYS} days, ${failed} failed.`}
            />
          </ChartCard>
        );
      }}
    </QueryBoundary>
  );
}

function PendingDecisionsCard() {
  const query = useQuery(listPendingDecisionsOptions());
  return (
    <Card
      title="Pending decisions"
      actions={
        <Link to="/agents/decisions" className="text-sm font-medium text-accent-text hover:underline">
          View all
        </Link>
      }
    >
      <QueryBoundary query={query} label="pending decisions" compact loading={<ListSkeleton />}>
        {(response) => {
          const decisions = response.data ?? [];
          if (decisions.length === 0) {
            return (
              <div className="py-6 text-center text-sm text-muted">
                <CheckCircle2 size={24} className="mx-auto mb-2 text-success" aria-hidden="true" />
                Nothing is waiting on you.
              </div>
            );
          }
          const items: ItemListItem[] = decisions.slice(0, 5).map((decision) => {
            const count = decision.memo.tradeCandidates.length;
            return {
              id: decision.id,
              leading: (
                <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-alt text-muted">
                  <Clock size={16} aria-hidden="true" />
                </div>
              ),
              title: decision.gateName || 'Decision required',
              subtitle: `${count} ${count === 1 ? 'candidate' : 'candidates'}`,
              value: <Badge variant={PRIORITY[decision.priority].variant}>{PRIORITY[decision.priority].label}</Badge>,
            };
          });
          return <ItemList items={items} aria-label="Pending decisions" />;
        }}
      </QueryBoundary>
    </Card>
  );
}

const RUN_STATUS: Record<string, { variant: BadgeVariant; label: string }> = {
  completed: { variant: 'success', label: 'Succeeded' },
  succeeded: { variant: 'success', label: 'Succeeded' },
  failed: { variant: 'danger', label: 'Failed' },
  running: { variant: 'info', label: 'Running' },
};

function RecentRunsCard() {
  const query = useQuery(listAgentWorkflowRunsOptions());
  const workflows = useQuery(listAgentWorkflowsOptions());
  const names = new Map((workflows.data?.data ?? []).map((workflow) => [workflow.id, workflow.name]));
  return (
    <Card
      title="Recent runs"
      actions={
        <Link to="/agents/runs" className="text-sm font-medium text-accent-text hover:underline">
          View all
        </Link>
      }
    >
      <QueryBoundary query={query} label="recent runs" compact loading={<ListSkeleton />}>
        {(response) => {
          const runs = [...(response.data ?? [])]
            .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
            .slice(0, 5);
          if (runs.length === 0) {
            return <p className="py-6 text-center text-sm text-muted">No workflow has run yet.</p>;
          }
          const items: ItemListItem[] = runs.map((run) => {
            const status = RUN_STATUS[run.status] ?? { variant: 'neutral' as const, label: run.status };
            return {
              id: run.id,
              leading: (
                <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-alt text-muted">
                  <Play size={16} aria-hidden="true" />
                </div>
              ),
              title: names.get(run.workflowId) ?? 'Workflow run',
              subtitle: new Date(run.startedAt).toLocaleString(),
              value: <Badge variant={status.variant}>{status.label}</Badge>,
            };
          });
          return <ItemList items={items} aria-label="Recent runs" />;
        }}
      </QueryBoundary>
    </Card>
  );
}

function ModulesCard() {
  const query = useQuery(listMyModulesOptions());
  const permissions = useAuthStore((s) => s.user?.permissions ?? []);
  const organizationPlan = useAuthStore((s) => s.user?.organizationPlan ?? null);
  return (
    <Card title="Your modules">
      <QueryBoundary
        query={query}
        label="your modules"
        compact
        loading={
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2" role="status" aria-label="Loading modules">
            <Skeleton height={96} rounded="rounded-lg" />
            <Skeleton height={96} rounded="rounded-lg" />
          </div>
        }
      >
        {(response) => {
          const modules = response.data ?? [];
          if (modules.length === 0) {
            return <p className="text-sm text-muted">No modules are enabled for your organization.</p>;
          }
          return (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {modules.map((mod) => {
                const Icon = resolveIcon(mod.icon);
                const entry = mod.nav.find(
                  (item) => !item.policy || evaluateClientPolicy(item.policy, permissions, organizationPlan),
                );
                const body = (
                  <>
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-surface-alt text-muted">
                      <Icon size={20} aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-heading">{mod.displayName}</p>
                      {mod.description && <p className="mt-1 line-clamp-2 text-xs text-muted">{mod.description}</p>}
                    </div>
                    {entry && <ArrowUpRight size={16} className="shrink-0 text-faint" aria-hidden="true" />}
                  </>
                );
                const cardClass = 'flex items-start gap-3 rounded-lg border border-card-border p-4';
                return entry ? (
                  <Link
                    key={mod.id}
                    to={entry.path}
                    aria-label={`Open ${mod.displayName}`}
                    className={`${cardClass} transition-colors hover:bg-surface-alt`}
                  >
                    {body}
                  </Link>
                ) : (
                  <div key={mod.id} className={cardClass}>
                    {body}
                  </div>
                );
              })}
            </div>
          );
        }}
      </QueryBoundary>
    </Card>
  );
}

function useHeadlineStats(): StatStripCell[] {
  const agents = useQuery(listActiveAgentsOptions());
  const decisions = useQuery(listPendingDecisionsOptions());
  const workflows = useQuery(listAgentWorkflowsOptions());
  const runs = useQuery(listAgentWorkflowRunsOptions());
  const count = (value: number | undefined) => (value === undefined ? '—' : value.toLocaleString());
  const pending = decisions.data?.data?.length;
  return [
    {
      label: 'Active agents',
      value: count(agents.data?.data?.filter((agent) => agent.status !== 'terminated').length),
      icon: Brain,
    },
    {
      label: 'Pending decisions',
      value: count(pending),
      icon: Clock,
      severity: pending !== undefined && pending > 0 ? 'warning' : undefined,
    },
    { label: 'Workflows', value: count(workflows.data?.data?.length), icon: Workflow },
    { label: 'Workflow runs', value: count(runs.data?.data?.length), icon: Play },
  ];
}

export const DashboardPage: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const stats = useHeadlineStats();
  const name = user?.fullName || user?.username || user?.email;

  return (
    <DashboardTemplate
      title={name ? `Welcome, ${name}` : 'Welcome'}
      subtitle="Your Conflui workspace at a glance."
      stats={stats}
    >
      <DashboardGrid>
        <div className="xl:col-span-8">
          <RunsChartCard />
        </div>
        <div className="xl:col-span-4">
          <PendingDecisionsCard />
        </div>
        <div className="xl:col-span-8">
          <ModulesCard />
        </div>
        <div className="xl:col-span-4">
          <RecentRunsCard />
        </div>
      </DashboardGrid>
    </DashboardTemplate>
  );
};
